import React, { useEffect, useState } from "react";
import axios from "axios";
import { createMRTColumnHelper } from "material-react-table";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Box, Tooltip, IconButton } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../../helper/urls";
import AdminCustomTable from "../../../components/custom_table/AdminCustomTable";

const CounselorCarousel = () => {
  const { token } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    avatar: "",
    bio: "",
    accomplishments: [""],
  });
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [carouselData, setCarouselData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result);
        setFormData((prev) => ({ ...prev, avatar: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAccomplishmentChange = (index, value) => {
    const updated = [...formData.accomplishments];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, accomplishments: updated }));
  };

  const addAccomplishmentField = () => {
    setFormData((prev) => ({
      ...prev,
      accomplishments: [...prev.accomplishments, ""],
    }));
  };

  const removeAccomplishmentField = (index) => {
    if (formData.accomplishments.length === 1) return;
    const updated = [...formData.accomplishments];
    updated.splice(index, 1);
    setFormData((prev) => ({ ...prev, accomplishments: updated }));
  };

  const fetchCarouselData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/dyn/carousel/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCarouselData(response.data.data);
    } catch (error) {
      console.error("Error fetching carousel:", error);
      toast.error("Failed to fetch carousel data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    form.append("name", formData.name);
    form.append("title", formData.title);
    form.append("bio", formData.bio);
    form.append("accomplishments", JSON.stringify(formData.accomplishments));

    const file = document.getElementById("imageUpload")?.files[0];
    if (file) {
      form.append("profile_image", file);
    }

    try {
      if (editingId) {
        await axios.post(
          `${baseUrl}/api/dyn/carousel/update/${editingId}`,
          form,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Profile updated successfully!");
      } else {
        await axios.post(`${baseUrl}/api/dyn/carousel/create`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Profile created successfully!");
      }

      setFormData({
        name: "",
        title: "",
        avatar: "",
        bio: "",
        accomplishments: [""],
      });
      setImagePreviewUrl("");
      setEditingId(null);
      fetchCarouselData();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit profile.");
    }
  };

  // Toggle blog publish/unpublish status
  const handleToggleStatus = async (id, currentStatus) => {
    setStatusLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await axios.put(
        `${baseUrl}/api/dyn/carousel/toggle/${id}`,
        { status: currentStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success || response.status === 200) {
        await fetchCarouselData();
        toast.success(
          `Counselor profile ${
            currentStatus == 0 ? "published" : "unpublished"
          } successfully!`
        );
      }
    } catch (error) {
      console.error("Error toggling counselor profile status:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update counselor profile status");
      }
    } finally {
      setStatusLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      title: item.title,
      avatar: "",
      bio: item.bio,
      accomplishments: item.accomplishments || [""],
    });
    // Prepend baseUrl for proper preview
    setImagePreviewUrl(
      item.profile_image ? `${baseUrl}${item.profile_image}` : ""
    );
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "You won't be able to revert this!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${baseUrl}/api/dyn/carousel/destroy/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Deleted successfully!");
        fetchCarouselData();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete.");
      }
    }
  };

  useEffect(() => {
    fetchCarouselData();
  }, []);

  const columnHelper = createMRTColumnHelper();
  const columns = [
    columnHelper.accessor("profile_image", {
      header: "Image",
      Cell: (cell) => {
        const imageUrl = cell.row.original?.profile_image;
        return imageUrl ? (
          <img
            src={`${baseUrl}${imageUrl}`}
            alt="Banner"
            style={{
              width: "50px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          "-"
        );
      },
    }),
    columnHelper.accessor("name", { header: "Name" }),
    columnHelper.accessor("title", { header: "Title" }),
    columnHelper.accessor("bio", {
      header: "Bio",
      Cell: (cell) => {
        const bio = cell.row.original?.bio || "";
        const words = bio.split(" ");
        const shortBio =
          words.length > 10 ? words.slice(0, 10).join(" ") + "..." : bio;
        return shortBio;
      },
    }),
    columnHelper.accessor("Actions", {
      header: "Actions",
      Cell: ({ cell }) => {
        const row = cell.row.original;
        const isPublished = row.status === 1;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={isPublished ? "Unpublish" : "Publish"}>
              <IconButton
                onClick={() => handleToggleStatus(row.id, row.status)}
                disabled={statusLoading[row.id]}
              >
                {statusLoading[row.id] ? (
                  <Icon
                    icon="mdi:loading"
                    className="animate-spin"
                    width="22"
                    height="22"
                  />
                ) : (
                  <Icon
                    icon={
                      isPublished ? "mdi:eye-outline" : "mdi:eye-off-outline"
                    }
                    color={isPublished ? "green" : "red"}
                    width="22"
                    height="22"
                  />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit" arrow>
              <button
                onClick={() => handleEdit(row)}
                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
              >
                <Icon icon="lucide:edit" />
              </button>
            </Tooltip>

            <Tooltip title="Delete" arrow>
              <button
                onClick={() => handleDelete(row.id)}
                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <Icon icon="mingcute:delete-2-line" />
              </button>
            </Tooltip>
          </Box>
        );
      },
    }),
  ];

  return (
    <>
      <ToastContainer />
      <div style={{ backgroundColor: "white", padding: "1rem" }}>
        <div className="counselor-container">
          <form className="counselor-form" onSubmit={handleSubmit}>
            <div className="counselor-row">
              <div className="avatar-section">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <label
                    htmlFor="imageUpload"
                    className="image-upload-label"
                    style={{ marginTop: "2rem" }}
                  >
                    <input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <div
                      className="avatar-preview"
                      style={{
                        backgroundImage: imagePreviewUrl
                          ? `url(${imagePreviewUrl})`
                          : "",
                      }}
                    ></div>
                    <Icon icon="solar:camera-outline" className="camera-icon" />
                  </label>
                </div>
              </div>
              <div className="info-section">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              required
            />
            <label>Accomplishments</label>
            {formData.accomplishments.map((acc, index) => (
              <div key={index} className="accomplishment-row">
                <input
                  type="text"
                  value={acc}
                  onChange={(e) =>
                    handleAccomplishmentChange(index, e.target.value)
                  }
                />

                <button
                  onClick={() => removeAccomplishmentField(index)}
                  disabled={formData.accomplishments.length === 1}
                  className="w-40-px h-40-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                  style={{ cursor: "pointer" }}
                >
                  <Icon icon="mingcute:delete-2-line" />
                </button>
                {index === formData.accomplishments.length - 1 && (
                  <button
                    onClick={addAccomplishmentField}
                    className="w-40-px h-40-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                  >
                    <Icon icon="mdi:plus" />
                  </button>
                )}
              </div>
            ))}
            <div className="btn-group d-flex gap-2 mt-3">
              <button type="submit" className="save-btn">
                {editingId ? "Update" : "Save"}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="cancel-btn btn btn-secondary"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({
                      name: "",
                      title: "",
                      avatar: "",
                      bio: "",
                      accomplishments: [""],
                    });
                    setImagePreviewUrl("");
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            {/* <div className="btn-group">
              <button type="submit" className="save-btn">
                {editingId ? "Update" : "Save"}
              </button>
            </div> */}
          </form>
        </div>
      </div>
      <div className="mt-10">
        <AdminCustomTable
          columns={columns}
          data={carouselData}
          loading={loading}
        />
      </div>
      {/* ✅ Embedded CSS */}
      <style>{`
          .counselor-container {
            max-width: 800px;
            margin: 20px auto;
            background: #fff;
            padding: 24px;
            border-radius: 12px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }

          .counselor-container h2 {
            text-align: center;
            margin-bottom: 20px;
          }

          .counselor-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .counselor-row {
            display: flex;
            flex-wrap: wrap;
            gap: 2rem;
          }

          .avatar-section {
            min-width: 130px;
            min-height: 130px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .info-section {
            flex: 2;
            display: flex;
            flex-direction: column;
          }

          .avatar-preview {
            width: 130px;
            height: 130px;
            border-radius: 50%;
            border: 2px solid #ccc;
            background-color: #eee;
            background-size: cover;
            background-position: center;
          }

          .camera-icon {
            position: relative;
            top: -43px;
            left: 98px;
            background: white;
            border-radius: 50%;
            font-size: 25px;
            padding: 2px;
          }
          .image-upload-label{
            height: 100%;
        
          }

          .image-upload-label input {
            display: none;
          }

          textarea, input {
            padding: 5px;
            border: 1px solid #ccc;
            border-radius: 6px;
            width: 100%;
          }

          label {
            font-weight: 500;
          }

          .accomplishment-row {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }

          .icon-btn {
            background: #f2f2f2;
            border: 1px solid #ccc;
            padding: 5px 10px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
          }

          .icon-btn:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          .btn-group {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          }

          .save-btn, .cancel-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-weight: bold;
          }

          .save-btn {
            background: #124178;
            color: white;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;

          }

          .cancel-btn {
            background: #f44336;
            color: white;
          }

          @media (max-width: 768px) {
            .counselor-row {
              flex-direction: column;
              align-items: center;
            }
            .camera-icon {
              left: 60px;
            }
          }
        `}</style>
      ;
    </>
  );
};

export default CounselorCarousel;
